export type UnitDimension =
  | 'mass'
  | 'dose_per_weight'
  | 'dose_per_area'
  | 'dose_per_day'
  | 'dose_per_weight_day'
  | 'dose_per_area_day'
  | 'dose_rate'
  | 'concentration'
  | 'auc'
  | 'time'
  | 'percent'
  | 'ratio';

interface UnitDefinition {
  code: string;
  dimension: UnitDimension;
  toCanonical: number;
}

const definitions: UnitDefinition[] = [
  { code: 'g', dimension: 'mass', toCanonical: 1000 },
  { code: 'mg', dimension: 'mass', toCanonical: 1 },
  { code: 'ug', dimension: 'mass', toCanonical: 0.001 },
  { code: 'ng', dimension: 'mass', toCanonical: 0.000001 },
  { code: 'g/kg', dimension: 'dose_per_weight', toCanonical: 1000 },
  { code: 'mg/kg', dimension: 'dose_per_weight', toCanonical: 1 },
  { code: 'ug/kg', dimension: 'dose_per_weight', toCanonical: 0.001 },
  { code: 'ng/kg', dimension: 'dose_per_weight', toCanonical: 0.000001 },
  { code: 'g/m2', dimension: 'dose_per_area', toCanonical: 1000 },
  { code: 'mg/m2', dimension: 'dose_per_area', toCanonical: 1 },
  { code: 'ug/m2', dimension: 'dose_per_area', toCanonical: 0.001 },
  { code: 'ng/m2', dimension: 'dose_per_area', toCanonical: 0.000001 },
  { code: 'g/day', dimension: 'dose_per_day', toCanonical: 1000 },
  { code: 'mg/day', dimension: 'dose_per_day', toCanonical: 1 },
  { code: 'ug/day', dimension: 'dose_per_day', toCanonical: 0.001 },
  { code: 'ng/day', dimension: 'dose_per_day', toCanonical: 0.000001 },
  { code: 'g/kg/day', dimension: 'dose_per_weight_day', toCanonical: 1000 },
  { code: 'mg/kg/day', dimension: 'dose_per_weight_day', toCanonical: 1 },
  { code: 'ug/kg/day', dimension: 'dose_per_weight_day', toCanonical: 0.001 },
  { code: 'ng/kg/day', dimension: 'dose_per_weight_day', toCanonical: 0.000001 },
  { code: 'g/m2/day', dimension: 'dose_per_area_day', toCanonical: 1000 },
  { code: 'mg/m2/day', dimension: 'dose_per_area_day', toCanonical: 1 },
  { code: 'ug/m2/day', dimension: 'dose_per_area_day', toCanonical: 0.001 },
  { code: 'ng/m2/day', dimension: 'dose_per_area_day', toCanonical: 0.000001 },
  { code: 'g/h', dimension: 'dose_rate', toCanonical: 1000 },
  { code: 'mg/h', dimension: 'dose_rate', toCanonical: 1 },
  { code: 'ug/h', dimension: 'dose_rate', toCanonical: 0.001 },
  { code: 'ng/h', dimension: 'dose_rate', toCanonical: 0.000001 },
  { code: 'g/L', dimension: 'concentration', toCanonical: 1000 },
  { code: 'mg/L', dimension: 'concentration', toCanonical: 1 },
  { code: 'ug/mL', dimension: 'concentration', toCanonical: 1 },
  { code: 'ug/L', dimension: 'concentration', toCanonical: 0.001 },
  { code: 'ng/mL', dimension: 'concentration', toCanonical: 0.001 },
  { code: 'ng/L', dimension: 'concentration', toCanonical: 0.000001 },
  { code: 'mg/mL', dimension: 'concentration', toCanonical: 1000 },
  { code: 'g/mL', dimension: 'concentration', toCanonical: 1000000 },
  { code: 'g*h/L', dimension: 'auc', toCanonical: 1000 },
  { code: 'mg*h/L', dimension: 'auc', toCanonical: 1 },
  { code: 'ug*h/mL', dimension: 'auc', toCanonical: 1 },
  { code: 'ug*h/L', dimension: 'auc', toCanonical: 0.001 },
  { code: 'ng*h/mL', dimension: 'auc', toCanonical: 0.001 },
  { code: 'ng*h/L', dimension: 'auc', toCanonical: 0.000001 },
  { code: 'mg*h/mL', dimension: 'auc', toCanonical: 1000 },
  { code: 'g*h/mL', dimension: 'auc', toCanonical: 1000000 },
  { code: 'h', dimension: 'time', toCanonical: 1 },
  { code: 'min', dimension: 'time', toCanonical: 1 / 60 },
  { code: '%', dimension: 'percent', toCanonical: 1 },
  { code: 'ratio', dimension: 'ratio', toCanonical: 1 },
];

const registry = new Map(definitions.map((definition) => [definition.code, definition]));

const aliasKey = (value: unknown): string => String(value || '')
  .trim()
  .replace(/[μµ]/g, 'u')
  .replace(/[渭碌]/g, 'u')
  .replace(/⋅/g, '·')
  .replace(/×/g, '*')
  .replace(/\s+/g, '')
  .toLowerCase();

const aliases: Record<string, string> = {
  gram: 'g', grams: 'g', 克: 'g',
  milligram: 'mg', milligrams: 'mg', 毫克: 'mg',
  microgram: 'ug', micrograms: 'ug', mcg: 'ug', 微克: 'ug',
  nanogram: 'ng', nanograms: 'ng', 纳克: 'ng',
  'mg/l': 'mg/L', mgperliter: 'mg/L', 'mg*l^-1': 'mg/L',
  'g/l': 'g/L', 'ug/l': 'ug/L', 'mcg/l': 'ug/L', 'ng/l': 'ng/L',
  'g/ml': 'g/mL', 'mg/ml': 'mg/mL', 'ug/ml': 'ug/mL', 'mcg/ml': 'ug/mL', 'ng/ml': 'ng/mL',
  'mg*h/l': 'mg*h/L', 'mg·h/l': 'mg*h/L', 'mg.h/l': 'mg*h/L',
  'g*h/l': 'g*h/L', 'g·h/l': 'g*h/L',
  'ug*h/l': 'ug*h/L', 'ug·h/l': 'ug*h/L', 'ug*h/ml': 'ug*h/mL',
  'ng*h/l': 'ng*h/L', 'ng·h/l': 'ng*h/L', 'ng*h/ml': 'ng*h/mL', 'ng·h/ml': 'ng*h/mL',
  'mg*h/ml': 'mg*h/mL', 'g*h/ml': 'g*h/mL',
  'g/m²': 'g/m2', 'g/m^2': 'g/m2',
  'mg/m²': 'mg/m2', 'mg/m^2': 'mg/m2',
  'ug/m²': 'ug/m2', 'ug/m^2': 'ug/m2',
  'ng/m²': 'ng/m2', 'ng/m^2': 'ng/m2',
  mg_kg: 'mg/kg', mg_m2: 'mg/m2',
  'mg/kg/24h': 'mg/kg/day', 'mg/m2/24h': 'mg/m2/day',
  'mg/m²/day': 'mg/m2/day', 'mg/m^2/day': 'mg/m2/day',
  minute: 'min', minutes: 'min', hr: 'h', hours: 'h',
  percent: '%', dimensionless: 'ratio', '1': 'ratio',
};

export const normalizeUnitCode = (value: unknown): string => {
  const raw = String(value || '').trim();
  if (registry.has(raw)) return raw;
  const key = aliasKey(raw);
  const alias = aliases[key];
  if (alias) return alias;
  const matching = definitions.find((definition) => aliasKey(definition.code) === key);
  if (matching) return matching.code;
  throw new Error(`不支持的临床单位：${raw || '（空）'}`);
};

export const unitDefinition = (value: unknown, expectedDimension?: UnitDimension): UnitDefinition => {
  const normalized = normalizeUnitCode(value);
  const definition = registry.get(normalized)!;
  if (expectedDimension && definition.dimension !== expectedDimension) {
    throw new Error(`Unit ${normalized} is ${definition.dimension}, expected ${expectedDimension}`);
  }
  return definition;
};

export const unitConversionFactor = (
  fromUnit: unknown,
  toUnit: unknown,
  expectedDimension?: UnitDimension,
): number => {
  const source = unitDefinition(fromUnit, expectedDimension);
  const target = unitDefinition(toUnit, expectedDimension);
  if (source.dimension !== target.dimension) {
    throw new Error(`不能将 ${source.code} 转换为 ${target.code}`);
  }
  return source.toCanonical / target.toCanonical;
};

export const convertUnitValue = (
  value: number,
  fromUnit: unknown,
  toUnit: unknown,
  expectedDimension?: UnitDimension,
): number => value * unitConversionFactor(fromUnit, toUnit, expectedDimension);

export const convertCanonicalValue = (
  value: unknown,
  toUnit: unknown,
  dimension: Extract<UnitDimension, 'mass' | 'dose_per_weight' | 'dose_rate' | 'concentration' | 'auc'>,
): number | null => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return null;
  const canonicalUnit: Record<typeof dimension, string> = {
    mass: 'mg',
    dose_per_weight: 'mg/kg',
    dose_rate: 'mg/h',
    concentration: 'mg/L',
    auc: 'mg*h/L',
  };
  return convertUnitValue(numeric, canonicalUnit[dimension], toUnit, dimension);
};

const CONCENTRATION_SERIES_KEYS = [
  'concentration',
  'concentrationP025',
  'concentrationP975',
  'concentration_p025',
  'concentration_p975',
  'concentrationP05',
  'concentrationP95',
  'concentration_p05',
  'concentration_p95',
  'concentrationMin',
  'concentrationMax',
  'concentration_min',
  'concentration_max',
] as const;

const projectConcentrationSeries = (series: any, factor: number): any => {
  if (!series || typeof series !== 'object') return series;
  const projected = { ...series };
  CONCENTRATION_SERIES_KEYS.forEach((key) => {
    if (Array.isArray(series[key])) {
      projected[key] = series[key].map((item: unknown) => (
        typeof item === 'number' && Number.isFinite(item) ? item * factor : item
      ));
    }
  });
  return projected;
};

/** Create a display-only curve copy from canonical mg/L values. */
export const projectCanonicalConcentrationCurve = <T>(
  curve: T,
  displayUnit: unknown,
): T => {
  if (!curve || typeof curve !== 'object') return curve;
  const factor = unitConversionFactor('mg/L', displayUnit || 'mg/L', 'concentration');
  const source = curve as any;
  const projected: any = projectConcentrationSeries(source, factor);
  [
    'currentPhase',
    'optimizedPhase',
    'continuationPhase',
    'current_phase',
    'optimized_phase',
    'continuation_phase',
  ].forEach((key) => {
    if (source[key]) projected[key] = projectConcentrationSeries(source[key], factor);
  });
  return projected as T;
};

/** Create display-only TDM points from canonical mg/L values. */
export const projectCanonicalTdmPoints = <T extends { time: number; concentration: number }>(
  points: T[] | null | undefined,
  displayUnit: unknown,
): T[] | undefined => {
  if (!Array.isArray(points)) return undefined;
  const factor = unitConversionFactor('mg/L', displayUnit || 'mg/L', 'concentration');
  return points.map((point) => ({
    ...point,
    concentration: Number(point.concentration) * factor,
  }));
};

/** Convert concentration-bearing input records to canonical mg/L values. */
export const canonicalizeTdmConcentrations = <T extends { concentration: number }>(
  records: T[] | null | undefined,
  inputUnit: unknown,
): T[] | undefined => {
  if (!Array.isArray(records)) return undefined;
  const factor = unitConversionFactor(inputUnit || 'mg/L', 'mg/L', 'concentration');
  return records.map((record) => ({
    ...record,
    concentration: Number(record.concentration) * factor,
  }));
};

export const displayUnitLabel = (unit: unknown): string => normalizeUnitCode(unit)
  .replace(/^ug/, 'µg')
  .replace('/m2', '/m²');

export const DOSE_MASS_UNIT_OPTIONS = ['g', 'mg', 'ug', 'ng'].map((value) => ({
  value,
  label: displayUnitLabel(value),
}));

export const CONCENTRATION_UNIT_OPTIONS = [
  'g/L', 'mg/L', 'ug/mL', 'ug/L', 'ng/mL', 'ng/L', 'mg/mL', 'g/mL',
].map((value) => ({ value, label: displayUnitLabel(value) }));

export const AUC_UNIT_OPTIONS = [
  'g*h/L', 'mg*h/L', 'ug*h/mL', 'ug*h/L', 'ng*h/mL', 'ng*h/L',
].map((value) => ({ value, label: displayUnitLabel(value) }));

export const getDoseMassUnit = (drugConfig: any): string => normalizeUnitCode(
  drugConfig?.units?.dose?.display_mass_unit || drugConfig?.dose_mass_unit || 'mg',
);

export const getDoseDisplayUnit = (drugConfig: any, basis?: string): string => {
  const mass = getDoseMassUnit(drugConfig);
  const resolvedBasis = basis || drugConfig?.units?.dose?.basis || drugConfig?.dose_basis || 'mg_kg';
  if (resolvedBasis === 'mg' || resolvedBasis === 'absolute') return displayUnitLabel(mass);
  if (resolvedBasis === 'mg_m2' || resolvedBasis === 'per_m2') return `${displayUnitLabel(mass)}/m²`;
  return `${displayUnitLabel(mass)}/kg`;
};

export const formatCanonicalDose = (
  valueMg: number | null | undefined,
  drugConfig: any,
  digits = 2,
): string => {
  if (valueMg == null || !Number.isFinite(Number(valueMg))) return '-';
  const unit = getDoseMassUnit(drugConfig);
  const displayValue = convertUnitValue(Number(valueMg), 'mg', unit, 'mass');
  return `${Number(displayValue.toFixed(digits))} ${displayUnitLabel(unit)}`;
};

export const unitFromResult = (
  result: any,
  quantity: 'dose' | 'concentration' | 'auc' | 'mic',
  fallback: string,
): string => {
  const context = result?.unit_context?.[quantity];
  const unit = quantity === 'dose'
    ? context?.display_mass_unit || context?.display_unit
    : context?.display_unit;
  try {
    return displayUnitLabel(unit || fallback);
  } catch {
    return fallback;
  }
};
